import { PHASES } from '../constants';
import { cardById } from '../deck';

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: state.meta.seq + 1 },
});

const logged = (state, text) => ({
  ...state,
  log: [...state.log, { round: state.meta.round, phase: state.meta.phase, text }],
});

const money = (thousands) => `$${(thousands * 1000).toLocaleString('en-US')}`;

/**
 * IELLO p7: "each chooses a house card from his hand and [places it] with
 * face hidden in front of him". Recorded rather than acted on - nothing
 * happens until everyone has picked, which is what makes it simultaneous.
 * A pick can be changed until the reveal.
 */
export const pick = (state, { playerId, cardId }) => {
  if (state.meta.phase !== PHASES.SELLING) return state;
  const player = state.players.find((p) => p.id === playerId);
  if (!player || !player.properties.includes(cardId)) return state;
  if (player.pick === cardId) return state;

  const next = clone(state);
  next.players = next.players.map((p) => (p.id === playerId ? { ...p, pick: cardId } : p));
  return next;
};

export const everyoneHasPicked = (state) => (
  state.meta.phase === PHASES.SELLING
  && !state.meta.roundOver
  && state.players.every((p) => p.pick !== null)
);

/**
 * The reveal. IELLO p7: "The player who played the highest value home takes
 * the highest cheque in the center of the table, the next one takes the
 * highest remaining cheque, and so on."
 *
 * House values are unique (1-30), so the ranking can never tie. Cheque
 * values can repeat, but two equal cheques are interchangeable, so sorting
 * settles everything. The houses played "are removed from the game" and the
 * cheques stay hidden until scoring.
 */
export const resolveSale = (state) => {
  if (!everyoneHasPicked(state)) return state;

  const ranked = [...state.players].sort(
    (a, b) => cardById(b.pick).value - cardById(a.pick).value,
  );
  const chequesByValue = [...state.market].sort(
    (a, b) => cardById(b).value - cardById(a).value,
  );

  const chequeFor = new Map(ranked.map((p, i) => [p.seat, chequesByValue[i]]));

  let next = clone(state);
  next.market = [];
  next.players = next.players.map((p) => ({
    ...p,
    properties: p.properties.filter((id) => id !== p.pick),
    cheques: [...p.cheques, chequeFor.get(p.seat)],
    pick: null,
  }));

  const results = ranked.map((p) => ({
    seat: p.seat,
    name: p.name,
    property: p.pick,
    cheque: chequeFor.get(p.seat),
  }));

  results.forEach(({ name, property, cheque }) => {
    next = logged(next, `${name} sold house ${cardById(property).value} for ${money(cardById(cheque).value)}.`);
  });

  const finished = next.chequeDeck.length === 0;
  if (finished) {
    next.meta.phase = PHASES.FINISHED;
    next = logged(next, 'All the cheques are gone - time to count the money.');
  }

  next.meta.roundOver = {
    kind: 'sale',
    round: next.meta.round,
    results,
    finished,
  };
  return next;
};
